import { useMemo } from 'react';
import moment from 'moment-timezone';
import {
  TIME_OPTIONS,
  parse12hrTime,
  formatTime12,
  findClosestTimeOption,
} from '../../fields/dateTime';

interface UseTimeOptionsArgs {
  selectedStartDate: Date | null;
  selectedStartTime: string;
  calendarBookedStartIso: string | null;
  scheduleTz: string;
}

export interface TimeOptionsResult {
  /** Filtered start-time options — past times hidden when date is today. */
  startTimeOptions: typeof TIME_OPTIONS;
  /** Filtered end-time options — only times after the selected start. */
  endTimeOptions: typeof TIME_OPTIONS;
  /** Minimum selectable date for the date picker. Advances to tomorrow
   *  when the selected start time is at or before the current wall clock. */
  datePickerMinDate: Date;
  /** The resolved effective start date (form value ?? calendar hint). */
  effectiveStartDate: Date | null;
  /** The resolved effective start time string (form value ?? calendar hint). */
  effectiveStartTimeStr: string;
}

export function useTimeOptions({
  selectedStartDate,
  selectedStartTime,
  calendarBookedStartIso,
  scheduleTz,
}: UseTimeOptionsArgs): TimeOptionsResult {
  const calendarHintTime12 =
    calendarBookedStartIso !== null && calendarBookedStartIso !== ''
      ? findClosestTimeOption(formatTime12(calendarBookedStartIso, scheduleTz))
      : '';

  const effectiveStartTimeStr = selectedStartTime || calendarHintTime12 || '';

  const effectiveStartDate: Date | null =
    selectedStartDate ??
    (calendarBookedStartIso ? new Date(calendarBookedStartIso) : null);

  const isSelectedDateToday = effectiveStartDate
    ? moment(effectiveStartDate)
        .tz(scheduleTz)
        .isSame(moment().tz(scheduleTz), 'day')
    : false;

  const nowMins =
    moment().tz(scheduleTz).hours() * 60 + moment().tz(scheduleTz).minutes();

  const startTimeParsedForMinDate = effectiveStartTimeStr
    ? parse12hrTime(effectiveStartTimeStr)
    : null;
  const selectedTimeMins = startTimeParsedForMinDate
    ? startTimeParsedForMinDate.hours * 60 + startTimeParsedForMinDate.minutes
    : -1;
  const isWallClockStartTimePastOrEqualNow =
    selectedTimeMins >= 0 && selectedTimeMins <= nowMins;

  const datePickerMinDate = useMemo(
    () =>
      isWallClockStartTimePastOrEqualNow
        ? moment().tz(scheduleTz).add(1, 'day').startOf('day').toDate()
        : moment().tz(scheduleTz).startOf('day').toDate(),
    [isWallClockStartTimePastOrEqualNow, scheduleTz]
  );

  const startTimeOptions = isSelectedDateToday
    ? TIME_OPTIONS.filter((opt) => {
        const p = parse12hrTime(opt.value);
        return p ? p.hours * 60 + p.minutes > nowMins : false;
      })
    : TIME_OPTIONS;

  const startTimeParsed = effectiveStartTimeStr
    ? parse12hrTime(effectiveStartTimeStr)
    : null;
  const startTimeMins = startTimeParsed
    ? startTimeParsed.hours * 60 + startTimeParsed.minutes
    : -1;

  const endTimeOptions =
    startTimeMins >= 0
      ? TIME_OPTIONS.filter((opt) => {
          const p = parse12hrTime(opt.value);
          return p ? p.hours * 60 + p.minutes > startTimeMins : false;
        })
      : TIME_OPTIONS;

  return {
    startTimeOptions,
    endTimeOptions,
    datePickerMinDate,
    effectiveStartDate,
    effectiveStartTimeStr,
  };
}
